/**
 * 权限服务 - v2版
 * @description 提供权限检查、角色管理、导航过滤等核心功能
 * @author Mech3D Team
 * @version v2
 */

import type { RouteLocationNormalized } from 'vue-router'
import { PERMISSION_GROUPS, PERMISSIONS } from '@/constants/permissions'
import { UserRole } from '@/types/auth'
import { sidebarNavigationService } from './SidebarNavigationService'

/**
 * 用户权限信息接口
 */
export interface UserPermissionInfo {
  role: UserRole
  permissions: string[]
  isLoggedIn: boolean
}

/**
 * 权限检查结果接口
 */
export interface PermissionCheckResult {
  allowed: boolean
  reason?: 'authorized' | 'not_logged_in' | 'missing_permission' | 'role_mismatch' | 'route_not_found'
  details?: string
  fallbackAction?: string
}

/**
 * 权限服务类
 */
export class PermissionService {
  private static instance: PermissionService

  // 角色默认权限映射
  private readonly rolePermissionsMap: Record<UserRole, string[]> = {
    [UserRole.GUEST]: [],
    [UserRole.USER]: [
      // 基础权限
      PERMISSIONS.USER.READ,
      PERMISSIONS.USER.UPDATE,
      PERMISSIONS.MODEL.VIEW,
      PERMISSIONS.TASK.VIEW,
      // 业务权限
      ...PERMISSION_GROUPS.USER_BUSINESS,
      // 新增：用户基础权限组
      ...PERMISSION_GROUPS.USER_BASE,
    ],
    [UserRole.DESIGNER]: [
      // 继承用户权限
      PERMISSIONS.USER.READ,
      PERMISSIONS.USER.UPDATE,
      PERMISSIONS.MODEL.VIEW,
      PERMISSIONS.TASK.VIEW,
      // 设计师特有权限
      PERMISSIONS.USER.CREATE,
      PERMISSIONS.MODEL.UPLOAD,
      PERMISSIONS.MODEL.MANAGE,
      PERMISSIONS.TASK.BID,
      PERMISSIONS.USER.TASKS.ACCEPT, // 🔧 核心修复：设计师可以承接任务
      // 业务权限
      ...PERMISSION_GROUPS.USER_BUSINESS,
      PERMISSIONS.USER.WALLET.WITHDRAW, // 提现
      // 新增：设计师专属权限组
      ...PERMISSION_GROUPS.DESIGNER_EXCLUSIVE,
    ],
    [UserRole.MANUFACTURER]: [
      // 继承用户权限
      PERMISSIONS.USER.READ,
      PERMISSIONS.USER.UPDATE,
      PERMISSIONS.MODEL.VIEW,
      PERMISSIONS.TASK.VIEW,
      // 制造服务商特有权限
      PERMISSIONS.MODEL.DOWNLOAD,
      PERMISSIONS.TASK.PUBLISH,
      PERMISSIONS.TASK.MANAGE,
      PERMISSIONS.USER.TASKS.ACCEPT, // 🔧 核心修复：制造服务商也可以承接任务
      // 业务权限
      ...PERMISSION_GROUPS.USER_BUSINESS,
      PERMISSIONS.USER.WALLET.WITHDRAW, // 提现
      // 新增：制造服务商专属权限组
      ...PERMISSION_GROUPS.MANUFACTURER_EXCLUSIVE,
    ],
    [UserRole.ADMIN]: [
      // 管理员拥有所有权限
      PERMISSIONS.ADMIN.READ,
      PERMISSIONS.ADMIN.WRITE,
      PERMISSIONS.ADMIN.DELETE,
      PERMISSIONS.USER_MANAGE,
      PERMISSIONS.MODEL.MANAGE,
      PERMISSIONS.TASK.MANAGE,
      PERMISSIONS.SYSTEM.MANAGE,
      PERMISSIONS.USER.TASKS.ACCEPT, // 🔧 核心修复：管理员拥有所有权限
      // 包含所有其他权限
      PERMISSIONS.USER.READ,
      PERMISSIONS.USER.UPDATE,
      PERMISSIONS.USER.CREATE,
      PERMISSIONS.USER.DELETE,
      PERMISSIONS.MODEL.VIEW,
      PERMISSIONS.MODEL.UPLOAD,
      PERMISSIONS.MODEL.DOWNLOAD,
      PERMISSIONS.TASK.VIEW,
      PERMISSIONS.TASK.PUBLISH,
      PERMISSIONS.TASK.BID,
      // 业务权限 - 全部
      ...PERMISSION_GROUPS.USER_BUSINESS,
      PERMISSIONS.USER.WALLET.WITHDRAW,
      // 新增：管理员拥有通配符权限
      PERMISSIONS.WILDCARD,
    ],
    [UserRole.SUPER_ADMIN]: [
      PERMISSIONS.WILDCARD,
    ],
  }

  private constructor() { }

  /**
   * 获取服务实例（单例模式）
   */
  static getInstance(): PermissionService {
    if (!PermissionService.instance) {
      PermissionService.instance = new PermissionService()
    }
    return PermissionService.instance
  }

  /**
   * 获取用户完整权限列表
   * @param role 用户角色
   * @param customPermissions 自定义权限
   * @returns 完整权限列表
   */
  public getUserFullPermissions(role: UserRole, customPermissions: string[]): string[] {
    const rolePermissions = this.rolePermissionsMap[role] || []
    // 合并角色权限和自定义权限，去重
    return [...new Set([...rolePermissions, ...customPermissions])]
  }

  /**
   * 检查权限继承关系
   * @param requiredPermission 需要的权限
   * @param userPermissions 用户拥有的权限
   * @returns 是否通过继承获得权限
   */
  private checkPermissionInheritance(requiredPermission: string, userPermissions: string[]): boolean {
    // 1. 检查通配符权限
    if (userPermissions.includes('*') || userPermissions.includes('*:*') || userPermissions.includes('*:*:*')) {
      return true
    }

    // 2. 解析权限结构 module:resource:action
    const parts = requiredPermission.split(':')
    if (parts.length !== 3) {
      // 简单权限格式 module:action
      if (parts.length === 2) {
        const [module, _action] = parts
        // 检查模块级通配符
        if (userPermissions.includes(`${module}:*`)) {
          return true
        }
      }
      return false
    }

    const [module, resource, action] = parts

    // 3. 检查模块级通配符 module:*
    if (userPermissions.includes(`${module}:*`)) {
      return true
    }

    // 4. 检查资源级通配符 module:resource:*
    if (userPermissions.includes(`${module}:${resource}:*`)) {
      return true
    }

    // 5. 检查特定操作的继承规则
    // 例如：manage 权限通常包含 read, write, delete
    if (action === 'view' || action === 'read') {
      if (userPermissions.includes(`${module}:${resource}:edit`)
        || userPermissions.includes(`${module}:${resource}:update`)
        || userPermissions.includes(`${module}:${resource}:manage`)) {
        return true
      }
    }

    if (action === 'edit' || action === 'update' || action === 'create' || action === 'add') {
      if (userPermissions.includes(`${module}:${resource}:manage`)) {
        return true
      }
    }

    // 6. 检查特殊业务逻辑继承
    // 比如：user:update 包含 user:profile:edit
    if (module === 'user' && resource === 'profile' && action === 'edit') {
      if (userPermissions.includes('user:update')) {
        return true
      }
    }

    return false
  }

  /**
   * 检查路径访问权限
   * @param path 路由路径
   * @param userInfo 用户权限信息
   * @returns 权限检查结果
   */
  checkPathPermission(path: string, userInfo: UserPermissionInfo): PermissionCheckResult {
    // 🔧 增强调试：初始状态检查

    // 🔧 增强调试：角色权限映射检查
    // const roleBasedPermissions = this.rolePermissionsMap[userInfo.role] || []
    const fullPermissions = this.getUserFullPermissions(userInfo.role, userInfo.permissions)

    // 🔧 修复：使用SidebarNavigationService获取导航项
    const navigationItem = sidebarNavigationService.getNavigationItem(path)

    if (!navigationItem) {
      return {
        allowed: true, // 如果导航配置中没有找到，允许访问（可能是公开页面）
        reason: 'route_not_found',
        details: 'Route not found in navigation config, assuming public access',
      }
    }

    // 🔧 增强调试：认证状态检查
    if (navigationItem.meta?.requiresAuth) {
      if (!userInfo.isLoggedIn) {
        return {
          allowed: false,
          reason: 'not_logged_in',
          details: 'Authentication required',
        }
      }
    }

    // 🔧 增强调试：权限要求检查
    if (navigationItem.permissions && navigationItem.permissions.length > 0) {
      const permissionResults = navigationItem.permissions.map((permission) => {
        const directMatch = fullPermissions.includes(permission)
        let inheritanceMatch = false
        // let inheritanceDetails = ''

        if (!directMatch) {
          inheritanceMatch = this.checkPermissionInheritance(permission, fullPermissions)
          if (inheritanceMatch) {
            // inheritanceDetails = '通过继承规则匹配'
          }
        }

        return {
          permission,
          directMatch,
          inheritanceMatch,
          allowed: directMatch || inheritanceMatch,
        }
      })

      const hasRequiredPermission = permissionResults.some(r => r.allowed)
      // const successfulPermissions = permissionResults.filter(r => r.allowed)
      // const failedPermissions = permissionResults.filter(r => !r.allowed)

      if (!hasRequiredPermission) {
        // 🔧 优化：简化权限检查失败日志，减少控制台干扰
        return {
          allowed: false,
          reason: 'missing_permission',
          details: `Missing required permissions: ${navigationItem.permissions.join(', ')}`,
        }
      }
    }

    return {
      allowed: true,
      reason: 'authorized',
    }
  }

  /**
   * 检查路由权限（Vue Router守卫使用）
   * @param route 路由对象
   * @param userInfo 用户权限信息
   * @returns 权限检查结果
   */
  checkRoutePermission(route: RouteLocationNormalized, userInfo: UserPermissionInfo): PermissionCheckResult {
    return this.checkPathPermission(route.path, userInfo)
  }

  /**
   * 检查是否拥有特定权限
   * @param permission 权限标识
   * @param userPermissions 用户权限列表
   * @returns 是否拥有权限
   */
  hasPermission(permission: string, userPermissions: string[] = []): boolean {
    // 1. 直接匹配
    if (userPermissions.includes(permission)) {
      return true
    }

    // 2. 继承匹配
    return this.checkPermissionInheritance(permission, userPermissions)
  }

  /**
   * 获取权限统计信息
   * @param userInfo 用户权限信息
   * @returns 权限统计
   */
  getPermissionStats(userInfo: UserPermissionInfo): {
    userCenter: { accessible: number, total: number }
    serviceCenter: { accessible: number, total: number }
    totalPermissions: number
  } {
    // 获取所有可访问的导航项
    // const userCenterItems = this.getAccessibleNavigationItems(userInfo, 'user')
    // const serviceCenterItems = this.getAccessibleNavigationItems(userInfo, 'service')

    const countAccessible = (groups: Array<{ children: unknown[] }>) => {
      let count = 0
      let total = 0
      groups.forEach((group) => {
        total += group.children.length
        count += group.children.length
      })
      return { accessible: count, total }
    }

    // 获取所有导航项总数（用于计算比例）
    const allGroups = sidebarNavigationService.getSidebarItems(
      this.getUserFullPermissions(userInfo.role, userInfo.permissions),
    )

    const allStats = countAccessible(allGroups)

    const fullPermissions = this.getUserFullPermissions(userInfo.role, userInfo.permissions)

    return {
      userCenter: allStats,
      serviceCenter: { accessible: 0, total: 0 }, // Service center stats are currently empty
      totalPermissions: fullPermissions.length,
    }
  }

  /**
   * 批量检查权限
   * @param paths 路径列表
   * @param userInfo 用户权限信息
   * @returns 权限检查结果映射
   */
  checkMultiplePermissions(
    paths: string[],
    userInfo: UserPermissionInfo,
  ): Record<string, PermissionCheckResult> {
    const results: Record<string, PermissionCheckResult> = {}
    paths.forEach((path) => {
      results[path] = this.checkPathPermission(path, userInfo)
    })
    return results
  }

  /**
   * 创建权限检查器函数
   * @param userInfo 用户权限信息
   * @returns 权限检查函数
   */
  createPermissionChecker(userInfo: UserPermissionInfo) {
    return (permission: string, value?: string | string[]): boolean => {
      if (Array.isArray(value)) {
        return value.some(p => this.hasPermission(p, userInfo.permissions))
      }
      else if (typeof value === 'string') {
        return this.hasPermission(value, userInfo.permissions)
      }
      else {
        return this.hasPermission(permission, userInfo.permissions)
      }
    }
  }

  /**
   * 获取可访问的导航项
   * @param userInfo 用户权限信息
   * @param moduleType 模块类型
   * @returns 导航分组列表
   */
  async getAccessibleNavigationItems(
    userInfo: UserPermissionInfo,
    moduleType: 'user' | 'service' = 'user',
  ) {
    // ✅ 修复：根据模块类型获取对应的导航分组
    const fullPermissions = this.getUserFullPermissions(userInfo.role, userInfo.permissions)
    const allGroups = sidebarNavigationService.getSidebarItems(fullPermissions)

    // 过滤出当前模块的分组
    const groups = allGroups.map(group => ({
      ...group,
      children: group.children.filter((item) => {
        // 根据meta.category判断所属模块
        // 注意：这里假设category命名规范，user_* 属于用户中心，service_* 属于服务中心
        if (moduleType === 'user') {
          return !item.meta.category.startsWith('service_')
        }
        else {
          return item.meta.category.startsWith('service_')
        }
      }).filter((item) => {
        // 再次进行权限检查，确保安全
        if (item.path) {
          const checkResult = this.checkPathPermission(item.path, userInfo)
          if (checkResult.allowed) {
            // 🔧 修复：如果允许访问，确保children也被正确过滤
            if (item.children && item.children.length > 0) {
              item.children = item.children.filter((child) => {
                if (child.path) {
                  return this.checkPathPermission(child.path, userInfo).allowed
                }
                return true
              })
            }
            return true
          }
          else {
            return false
          }
        }
        // 如果没有path（如分组标题），检查是否有可访问的子项
        if (item.children && item.children.length > 0) {
          const validChildren = item.children.filter((child) => {
            if (child.path) {
              return this.checkPathPermission(child.path, userInfo).allowed
            }
            return true
          })
          return validChildren.length > 0
        }
        return true
      }),
    })).filter(group => group.children.length > 0) // 移除空分组

    // let totalItems = 0
    // let filteredItems = 0

    interface FilterableNavItem {
      title: string
      path?: string
      children?: FilterableNavItem[]
      meta?: { category: string }
    }

    const filterNavigationItems = (items: FilterableNavItem[], level = 0): FilterableNavItem[] => {
      // const indent = '  '.repeat(level)

      return items.filter((item) => {
        // totalItems++
        // const itemInfo = `${item.title} (${item.path || 'no-path'})`

        if (item.children) {
          // 递归过滤子项
          const filteredChildren = filterNavigationItems(item.children, level + 1)

          // 如果所有子项都被过滤掉，则隐藏父项
          if (filteredChildren.length === 0) {
            return false
          }

          // 更新子项
          item.children = filteredChildren
          // filteredItems++

          return true
        }

        // 检查当前项的权限
        if (item.path) {
          const checkResult = this.checkPathPermission(item.path, userInfo)
          if (checkResult.allowed) {
            // filteredItems++
            return true
          }
          else {
            return false
          }
        }

        // 没有路径的分组项，只要有子项就显示
        const hasChildren = item.children && item.children.length > 0
        if (hasChildren) {
          // filteredItems++
        }
        return hasChildren
      })
    }

    const result = filterNavigationItems(groups)

    return result
  }
}

/**
 * 导出服务实例
 */
export const permissionService = PermissionService.getInstance()
